import { useMutation, useQuery } from "@apollo/client/react"
import React, { useState } from "react"
import { toast } from "sonner"
import { gql } from "~/__generated__"
import { AccountSettingsQuery } from "~/__generated__/graphql"
import { useDocumentTitle } from "~/common/use-document-title"
import { GraphqlError } from "~/ui/graphql-error"

const ACCOUNT_SETTINGS_QUERY = gql(/* GraphQL */ `
  query AccountSettings {
    viewer {
      id
      firstName
      lastName
      email
      digestPreference
      userLocations {
        id
        position
        cityZip
        radius
      }
    }
  }
`)

const ACCOUNT_SETTINGS_UPDATE_MUTATION = gql(/* GraphQL */ `
  mutation AccountSettingsUpdate($input: AccountSettingsUpdateInput!) {
    accountSettingsUpdate(input: $input) {
      user {
        id
        digestPreference
        userLocations {
          id
          position
          cityZip
          radius
        }
      }
    }
  }
`)

const DIGEST_OPTIONS = [
  { value: "DAILY", label: "Daily", description: "Sends at 6:30pm your local time" },
  { value: "WEEKLY", label: "Weekly", description: "Sends Thursday at 6:30pm your local time" },
  { value: "OFF", label: "Off", description: "No digest emails" },
] as const

const RADIUS_OPTIONS = [
  { value: "everywhere", label: "Everywhere" },
  { value: "25mi", label: "25 miles" },
  { value: "50mi", label: "50 miles" },
  { value: "100mi", label: "100 miles" },
] as const

type LocationState = { cityZip: string; radius: string }
type Viewer = NonNullable<AccountSettingsQuery["viewer"]>

function buildInitialLocations(viewer: Viewer): [LocationState, LocationState] {
  const locs: [LocationState, LocationState] = [
    { cityZip: "", radius: "everywhere" },
    { cityZip: "", radius: "everywhere" },
  ]
  for (const loc of viewer.userLocations) {
    const idx = loc.position - 1
    if (idx === 0 || idx === 1) {
      locs[idx] = { cityZip: loc.cityZip, radius: loc.radius }
    }
  }
  return locs
}

const SettingsForm: React.FC<{ viewer: Viewer }> = ({ viewer }) => {
  const [digestPreference, setDigestPreference] = useState<string>(viewer.digestPreference)
  const [locations, setLocations] = useState<[LocationState, LocationState]>(() =>
    buildInitialLocations(viewer)
  )
  const [accountSettingsUpdate, { loading: saving }] = useMutation(ACCOUNT_SETTINGS_UPDATE_MUTATION)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    const locationsInput = locations
      .map((loc, i) => ({ position: i + 1, cityZip: loc.cityZip, radius: loc.radius }))
      .filter((loc) => loc.cityZip.trim().length > 0)

    await accountSettingsUpdate({
      variables: {
        input: {
          digestPreference: digestPreference as "DAILY" | "WEEKLY" | "OFF",
          locations: locationsInput,
        },
      },
      onError: (err) => toast.error(err.message),
      onCompleted: () => toast.success("Settings saved"),
    })
  }

  return (
    <form onSubmit={handleSave} className="flex-1 overflow-y-auto">
      <div className="max-w-lg px-6 py-6 space-y-8">
        <section>
          <p className="text-[9px] font-semibold tracking-[3px] uppercase text-stone-400 mb-3">
            Email Digest
          </p>
          <div className="space-y-2">
            {DIGEST_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className="flex items-start gap-3 p-3 border border-stone-200 bg-white cursor-pointer hover:border-stone-400 transition-colors"
              >
                <input
                  type="radio"
                  name="digestPreference"
                  value={opt.value}
                  checked={digestPreference === opt.value}
                  onChange={() => setDigestPreference(opt.value)}
                  className="mt-0.5 shrink-0"
                />
                <div>
                  <p className="text-xs font-semibold text-stone-900">{opt.label}</p>
                  <p className="text-[11px] text-stone-400 mt-0.5">{opt.description}</p>
                </div>
              </label>
            ))}
          </div>
        </section>

        <section>
          <p className="text-[9px] font-semibold tracking-[3px] uppercase text-stone-400 mb-1">
            Location Filter
          </p>
          <p className="text-[11px] text-stone-400 mb-3">
            Narrow results to auctions near you. Leave blank for all locations.
          </p>
          <div className="space-y-3">
            {([0, 1] as const).map((idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="text-[10px] text-stone-400 w-4 shrink-0">{idx + 1}</span>
                <input
                  type="text"
                  placeholder="City or zip code"
                  value={locations[idx].cityZip}
                  onChange={(e) => {
                    const next: [LocationState, LocationState] = [locations[0], locations[1]]
                    next[idx] = { ...next[idx], cityZip: e.target.value }
                    setLocations(next)
                  }}
                  className="flex-1 text-[12px] border border-stone-200 bg-white px-3 py-2 outline-none focus:border-stone-400 placeholder:text-stone-300"
                />
                <select
                  value={locations[idx].radius}
                  onChange={(e) => {
                    const next: [LocationState, LocationState] = [locations[0], locations[1]]
                    next[idx] = { ...next[idx], radius: e.target.value }
                    setLocations(next)
                  }}
                  className="text-[12px] border border-stone-200 bg-white px-2 py-2 outline-none focus:border-stone-400"
                >
                  {RADIUS_OPTIONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </section>

        <button
          type="submit"
          disabled={saving}
          className="px-6 py-2.5 bg-stone-900 text-stone-50 text-[12px] tracking-wide hover:bg-stone-700 transition-colors disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>
    </form>
  )
}

export const AccountSettingsScreen: React.FC = () => {
  useDocumentTitle("Account Settings")
  const { data, loading, error } = useQuery(ACCOUNT_SETTINGS_QUERY)

  if (error) return <GraphqlError error={error} />

  return (
    <div className="flex flex-1 flex-col min-h-0 bg-stone-50">
      <div className="px-6 py-5 border-b border-stone-200 bg-white">
        <h1
          className="text-sm font-bold text-stone-900"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Account Settings
        </h1>
      </div>
      {loading || !data?.viewer ? (
        <div className="px-6 py-8 text-sm text-stone-400">Loading…</div>
      ) : (
        <SettingsForm viewer={data.viewer} />
      )}
    </div>
  )
}
